const IDENTITY_SCOPES = 'openid https://www.googleapis.com/auth/userinfo.email https://www.googleapis.com/auth/userinfo.profile';
const PENDING = 'sp_supabase_oauth';

// Only this known application error gets specific copy; never render provider
// messages or callback error descriptions as account guidance.
export function googleOAuthErrorView(error) {
  if (error?.message === 'Use the same account to connect Google data') return {
    title: 'Google data connection couldn’t be completed.',
    message: 'Use the same account to connect Google data. To connect a different account, sign out, sign in to SEO Signals with the Google account you intend to use, then reconnect Google data.',
  };
  return {title:'Google sign-in couldn’t be completed.',message:'Please try again, or sign in another way.'};
}

export function googleOAuthReady(config, intent = 'login') {
  return (intent === 'connect' ? config?.data_configured ?? config?.configured : config?.login_configured ?? config?.configured) === true;
}
function readPending(storage) {
  let pending;
  try { pending = JSON.parse(storage.getItem(PENDING)); } catch {}
  if (!pending || !['login', 'connect'].includes(pending.intent) ||
      !Number.isFinite(pending.startedAt) || Date.now() - pending.startedAt > 600000 || pending.startedAt > Date.now()) return null;
  return pending;
}
// Capture only the operation for the retry UI before clearing provider state.
// Retrying always creates a fresh PKCE request and rechecks the current account.
export function pendingGoogleIntent(storage) { return readPending(storage)?.intent || 'login'; }

// The SDK owns PKCE verification. This record binds the application operation
// to its initiating tab/account; it is not a substitute for OAuth state/PKCE.
export function createGoogleOAuth({ storage, getClient, origin, getUser, saveSession, connectData }) {
  let completing;
  const clear = () => {
    storage.removeItem(PENDING);
    storage.removeItem('sp_supabase_auth');
    storage.removeItem('sp_supabase_auth-code-verifier');
    // auth-js 2.117 also writes indexed flow slots while retaining the fixed
    // verifier for compatibility. One app operation is allowed per tab; clear
    // this client's slots (including orphaned slots) on every terminal path.
    // Iterate backwards because removing a Storage key changes its indices.
    for (let i = storage.length - 1; i >= 0; i--) {
      const key = storage.key(i);
      if (key && /^sp_supabase_auth-flow-[a-zA-Z0-9_-]{8,64}-code-verifier$/.test(key)) storage.removeItem(key);
    }
    storage.removeItem('sp_supabase_auth-flows-code-verifier');
  };
  return {
    clear,
    async start(config, intent = 'login') {
      if (config.auth_mode !== 'supabase' || (!['login', 'connect'].includes(intent) || !googleOAuthReady(config, intent))) throw new Error('Google sign-in is not configured');
      const user = intent === 'connect' ? await getUser() : null;
      if (intent === 'connect' && (!user?.id || user.is_demo)) throw new Error('Sign in before connecting Google data');
      completing = undefined;
      const client = await getClient(config);
      storage.setItem(PENDING, JSON.stringify({intent,userId:user?.id,startedAt:Date.now()}));
      try {
        const {error} = await client.auth.signInWithOAuth({provider:'google',options:{
          redirectTo:origin(),
          scopes:intent === 'login' ? IDENTITY_SCOPES : (config.data_scopes || config.scopes).join(' '),
          ...(intent === 'connect' ? {queryParams:{access_type:'offline',prompt:'consent'}} : {}),
        }});
        if (error) throw error;
      } catch (error) { clear(); throw error; }
    },
    complete(code) {
      if (completing) return completing;
      completing = (async()=>{
        try {
          const pending = readPending(storage);
          if (!code || !pending) throw new Error('Start Google sign-in again');
          const client=await getClient();
          const {data,error}=await client.auth.exchangeCodeForSession(code);
          if(error || !data?.session) throw new Error('Google sign-in could not be completed');
          const signed=data.session;
          let result={connected:false};
          if(pending.intent === 'connect') {
            if (!pending.userId || signed.user.id !== pending.userId) throw new Error('Use the same account to connect Google data');
            // Keep the existing app identity if attachment fails. The server
            // independently checks the provider identity against that user.
            if(!signed.provider_token) throw new Error('Google data access was not granted');
            result=await connectData({access_token:signed.provider_token,refresh_token:signed.provider_refresh_token});
          }
          saveSession({token:signed.access_token,refresh_token:signed.refresh_token,expires_at:signed.expires_at,email:signed.user.email});
          return result;
        } finally { clear(); }
      })();
      return completing;
    },
  };
}
